/**
 * Stock-on-hand logic for the console Stock screen (F5.6): totals, per-lot grouping, dock age
 * and the CSV export rows. Pure — the page feeds it mapped rows from api/store.ts.
 */
import type { StockRow } from '../api/store'
import { kgToGrams, num, obj } from '../api/mappers'

export interface StockTotals {
  bags: number
  kgNowG: number
  kgAtGateG: number
  /** 'all' = totals for every row matching the filters (server summary); 'page' = this page only. */
  scope: 'all' | 'page'
}

/**
 * Totals for the summary tiles: the server's `summary` ({ bags, kg_now, kg_at_gate }) when it
 * came back, else the sum of the rows on screen.
 */
export function stockTotals(summary: unknown, rows: ReadonlyArray<StockRow>): StockTotals {
  const s = obj(summary)
  const kgNow = kgToGrams((s.kg_now ?? s.kgNow) as string | number | undefined)
  const kgGate = kgToGrams((s.kg_at_gate ?? s.kgAtGate) as string | number | undefined)
  if (s.bags !== undefined && kgNow !== null && kgGate !== null) {
    return { bags: num(s.bags), kgNowG: kgNow, kgAtGateG: kgGate, scope: 'all' }
  }
  return {
    bags: rows.reduce((a, r) => a + r.bags, 0),
    kgNowG: rows.reduce((a, r) => a + r.kgNowG, 0),
    kgAtGateG: rows.reduce((a, r) => a + r.kgAtGateG, 0),
    scope: 'page',
  }
}

export interface LotStock {
  unitId: string
  unitCode: string
  stream: string
  unitHold: string
  holdReasons: string[]
  bags: number
  bagsOnHold: number
  kgNowG: number
  kgAtGateG: number
  /** Oldest days-in-store across its places. */
  daysInStore: number | null
  places: Array<{ key: string; kind: 'LOCATION' | 'TRIP'; label: string; shipmentId: string | null; bags: number }>
  /** The rows the lot was built from (for its status). */
  rows: StockRow[]
}

/** One line per lot (in first-seen order): bags and kg summed, places listed. */
export function groupStockByLot(rows: ReadonlyArray<StockRow>): LotStock[] {
  const byLot = new Map<string, LotStock>()
  for (const r of rows) {
    let lot = byLot.get(r.unitId)
    if (!lot) {
      lot = {
        unitId: r.unitId,
        unitCode: r.unitCode,
        stream: r.stream,
        unitHold: r.unitHold,
        holdReasons: r.holdReasons,
        bags: 0,
        bagsOnHold: 0,
        kgNowG: 0,
        kgAtGateG: 0,
        daysInStore: null,
        places: [],
        rows: [],
      }
      byLot.set(r.unitId, lot)
    }
    lot.bags += r.bags
    lot.bagsOnHold += r.bagsOnHold
    lot.kgNowG += r.kgNowG
    lot.kgAtGateG += r.kgAtGateG
    if (r.daysInStore !== null) lot.daysInStore = Math.max(lot.daysInStore ?? 0, r.daysInStore)
    lot.places.push({
      key: r.key,
      kind: r.placeKind,
      label: placeLabel(r),
      shipmentId: r.shipmentId,
      bags: r.bags,
    })
    lot.rows.push(r)
  }
  return [...byLot.values()]
}

/** Where a stock row is: the place path, or the trip it is on. */
export function placeLabel(r: Pick<StockRow, 'placeKind' | 'locationPath' | 'locationCode' | 'shipmentCode'>): string {
  if (r.placeKind === 'TRIP') return r.shipmentCode ? `On trip ${r.shipmentCode}` : 'On a trip'
  return r.locationPath ?? r.locationCode ?? '—'
}

/** Dock bags waiting longer than this are flagged. */
export const DOCK_STALE_MS = 24 * 3600_000

export function isDockStale(since: string | null, now: number = Date.now()): boolean {
  if (!since) return false
  const t = Date.parse(since.replace(' ', 'T'))
  return Number.isFinite(t) && now - t > DOCK_STALE_MS
}

/** grams → "46.100" for exports. */
const kg = (g: number) => (g / 1000).toFixed(3)

export const STOCK_CSV_HEADER = [
  'Lot',
  'Stream',
  'Place',
  'Trip',
  'Bags',
  'Bags on hold',
  'Kg now',
  'Kg at gate',
  'Days in store',
  'Status',
]

export function stockCsvRows(rows: ReadonlyArray<StockRow>, status: (r: StockRow) => string): unknown[][] {
  return rows.map((r) => [
    r.unitCode,
    r.stream === 'CONV' ? 'Ordinary' : 'Good',
    r.placeKind === 'LOCATION' ? (r.locationPath ?? r.locationCode ?? '') : '',
    r.placeKind === 'TRIP' ? (r.shipmentCode ?? '') : '',
    r.bags,
    r.bagsOnHold,
    kg(r.kgNowG),
    kg(r.kgAtGateG),
    r.daysInStore ?? '',
    status(r),
  ])
}
